"use client";

import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const statsData = [{
  value: "100%",
  label: "CLIENT SATISFACTION"
}, {
  value: "150+",
  label: "PROJECTS COMPLETED"
}, {
  value: "42+",
  label: "YEARS’ EXPERIENCE"
}, {
  value: "100%",
  label: "SAFETY & COMPLIANCE"
}];
export default function RealResultsForDelhiClinics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="223377" id="sector-expertise-metrics" className="dark relative py-24 lg:py-32 bg-[hsl(0,0%,5%)] overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 opacity-[0.02] pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641543/img/delhi_map_subtle_bg.png)] bg-cover bg-center bg-no-repeat mix-blend-overlay" />

      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="absolute top-1/4 left-[-10%] w-[40%] h-[40%] bg-primary/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div ref={ref} className={`container relative z-10 mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-24">
          <h2 className="text-3xl md:text-5xl font-extrabold font-default text-white mb-6 tracking-widest uppercase">
            Combined Company Sector Experience
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default max-w-2xl mx-auto">
            Delivering high‑quality commercial and industrial solutions across Australia with precision and reliability.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {statsData.map((stat, i) => <div data-index={i} key={i} className="h-full">
              <Card className="h-full bg-transparent border-0 border-l border-primary/40 rounded-none shadow-none transition-all duration-300 hover:bg-white/5">
                <CardContent className="flex flex-col items-center lg:items-start justify-center p-8 md:p-10 text-center lg:text-left h-full">
                  <span className="text-6xl md:text-7xl lg:text-8xl font-black font-default text-white mb-4 tracking-tighter">
                    {stat.value}
                  </span>
                  <span className="text-xs md:text-sm text-primary font-bold font-mono tracking-widest uppercase leading-snug">
                    {stat.label}
                  </span>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}